import { useEnvironmentActions } from "@/actions/use-environment-actions";
import { IconPlay } from "@/assets/icons/geist/IconPlay";
import { IconStop } from "@/assets/icons/geist/IconStop";
import { revealServicesAtom } from "@/atoms/ui-state";
import { commandItemIconClass } from "@/components/command-palette/constants";
import { SYSTEM_PORT_NAMES } from "@/components/command-palette/constants";
import { CommandGroup, CommandItem, CommandLoading, CommandSeparator } from "@/components/flexkit/Command";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { useSessionCommandPaletteActions } from "@/hooks/use-session-command-palette-actions";
import { serviceToggleAction, useToggleService } from "@/hooks/use-toggle-service";
import { isManualTask, useListServices, useListTasks, useStartTask } from "@/queries/automations-queries";
import { useEnvironment } from "@/queries/environment-queries";
import { NAV_STATE_DETAILS } from "@/routes/environments/details/environment-nav-state";
import { canOpen } from "@/routes/environments/phase";
import { formatError } from "@/utils/errors";
import { IconArrowRight } from "central-icons/IconArrowRight";
import { useSetAtom } from "jotai";
import { useCallback, useMemo, type FC } from "react";
import type { NavigateFunction } from "react-router-dom";

const noop = () => undefined;

type EnvironmentPageProps = {
    environmentId: string;
    runCommand: (fn: () => void) => void;
    runAsyncCommand: (fn: () => Promise<void>) => void;
    navigate: NavigateFunction;
};

/**
 * Entity-level page in the command palette tree. Shows all actions
 * available for a specific environment: lifecycle, editor, ports,
 * services, tasks, and troubleshooting.
 */
export const EnvironmentPage: FC<EnvironmentPageProps> = ({ environmentId, runCommand, runAsyncCommand, navigate }) => {
    const { data: environment, isPending: envPending } = useEnvironment(environmentId);
    const { toast } = useToast();

    // Session troubleshooting actions (copy session ID, download support bundle).
    // "Reset conversation" is omitted here — it requires a confirmation modal that
    // can't survive the command palette closing. It's available via the dynamic
    // registration path in useRegisterEnvironmentCommands instead.
    const { troubleshootingActions: sessionTroubleshootingActions } = useSessionCommandPaletteActions(environmentId);

    // Core actions from the unified hook
    const actions = useEnvironmentActions(environmentId);

    // Ports
    const portActions = useMemo(() => {
        if (!environment || !canOpen(environment)) return [];
        const specPorts = environment.spec?.ports ?? [];
        const statusPorts = environment.status?.environmentUrls?.ports ?? [];
        return specPorts
            .filter((spec) => !SYSTEM_PORT_NAMES.has(spec.name))
            .map((spec) => {
                const status = statusPorts.find((p) => p.port === spec.port);
                return {
                    id: `port-${spec.port}`,
                    label: spec.name ? `${spec.name} (:${spec.port})` : `Port ${spec.port}`,
                    url: status?.url,
                };
            });
    }, [environment]);

    // Services
    const { data: services } = useListServices(environmentId);
    const toggleService = useToggleService(environmentId);
    const setRevealServices = useSetAtom(revealServicesAtom);

    // Tasks
    const { data: tasks } = useListTasks(environmentId);
    const startTask = useStartTask();
    const handleStartTask = useCallback(
        async (taskId: string) => {
            try {
                await startTask.mutateAsync(taskId);
            } catch (error) {
                toast({ title: "Failed to start task", description: formatError(error) });
            }
        },
        [startTask, toast],
    );

    if (envPending) {
        return <CommandLoading>Loading environment...</CommandLoading>;
    }

    // Partition core actions by group for structured rendering
    const actionsByGroup = new Map<string, typeof actions>();
    for (const action of actions) {
        if (action.hidden) continue;
        const group = action.group ?? "default";
        if (!actionsByGroup.has(group)) actionsByGroup.set(group, []);
        actionsByGroup.get(group)!.push(action);
    }

    const renderActionGroup = (groupKey: string, heading: string) => {
        const groupActions = actionsByGroup.get(groupKey);
        if (!groupActions || groupActions.length === 0) return null;
        return (
            <CommandGroup heading={heading}>
                {groupActions.map((action) => (
                    <CommandItem
                        key={action.id}
                        value={action.label}
                        keywords={action.keywords}
                        disabled={action.disabled}
                        onSelect={() => {
                            const result = action.onSelect();
                            if (result instanceof Promise) {
                                runAsyncCommand(() => result);
                            } else {
                                runCommand(noop);
                            }
                        }}
                    >
                        {action.icon && <span className={commandItemIconClass}>{action.icon}</span>}
                        <span>{action.label}</span>
                    </CommandItem>
                ))}
            </CommandGroup>
        );
    };

    return (
        <>
            {renderActionGroup("navigate", "Navigate")}
            {renderActionGroup("lifecycle", "Lifecycle")}

            {/* Ports */}
            {portActions.length > 0 && (
                <CommandGroup heading="Ports">
                    {portActions.map((port) => (
                        <CommandItem
                            key={port.id}
                            value={port.label}
                            keywords={["port", "open"]}
                            disabled={!port.url}
                            onSelect={() => {
                                if (port.url) {
                                    runCommand(() => window.open(port.url, "_blank", "noopener,noreferrer"));
                                }
                            }}
                        >
                            <IconArrowRight className={commandItemIconClass} />
                            <span>{port.label}</span>
                        </CommandItem>
                    ))}
                </CommandGroup>
            )}

            {/* Services */}
            {services && services.length > 0 && (
                <CommandGroup heading="Services">
                    {services.map((service) => {
                        const name = service.metadata?.name ?? service.id;
                        const action = serviceToggleAction(service);

                        if (action === "start" || action === "stop") {
                            const shouldStart = action === "start";
                            return (
                                <CommandItem
                                    key={service.id}
                                    value={name}
                                    keywords={["service", action, shouldStart ? "enable" : "disable"]}
                                    onSelect={() =>
                                        runAsyncCommand(async () => {
                                            navigate(`/details/${environmentId}`, NAV_STATE_DETAILS);
                                            setRevealServices((c) => c + 1);
                                            await toggleService(service, shouldStart);
                                        })
                                    }
                                >
                                    {shouldStart ? (
                                        <IconPlay size="sm" className={commandItemIconClass} />
                                    ) : (
                                        <IconStop size="sm" className={commandItemIconClass} />
                                    )}
                                    <span>{`${shouldStart ? "Start" : "Stop"} ${name}`}</span>
                                </CommandItem>
                            );
                        }
                        return (
                            <CommandItem
                                key={service.id}
                                value={name}
                                keywords={["service", "automation"]}
                                onSelect={() =>
                                    runCommand(() =>
                                        navigate(
                                            `/details/${environmentId}/services/${service.id}/logs`,
                                            NAV_STATE_DETAILS,
                                        ),
                                    )
                                }
                            >
                                <IconArrowRight className={commandItemIconClass} />
                                <span>{name}</span>
                            </CommandItem>
                        );
                    })}
                </CommandGroup>
            )}

            {/* Tasks */}
            {tasks && tasks.length > 0 && (
                <CommandGroup heading="Tasks">
                    {tasks.map((task) => {
                        const manual = isManualTask(task);
                        return (
                            <CommandItem
                                key={task.id}
                                value={task.metadata?.name ?? task.id}
                                keywords={["task", "automation", "run"]}
                                onSelect={() => {
                                    if (manual) {
                                        runAsyncCommand(() => handleStartTask(task.id));
                                    } else {
                                        runCommand(() =>
                                            navigate(`/details/${environmentId}/task/${task.id}`, NAV_STATE_DETAILS),
                                        );
                                    }
                                }}
                            >
                                <IconArrowRight className={commandItemIconClass} />
                                <span>{task.metadata?.name ?? task.id}</span>
                                {manual && <span className="ml-auto text-xs text-content-tertiary">Run</span>}
                            </CommandItem>
                        );
                    })}
                </CommandGroup>
            )}

            {renderActionGroup("open", "IDEs")}

            <CommandSeparator />

            {renderActionGroup("edit", "Edit")}
            {renderActionGroup("info", "Info")}

            {/* Troubleshooting */}
            <CommandGroup heading="Troubleshooting">
                {sessionTroubleshootingActions.map((action) => (
                    <CommandItem
                        key={action.id}
                        value={action.label}
                        keywords={action.keywords}
                        disabled={action.disabled}
                        onSelect={() => runAsyncCommand(() => Promise.resolve(action.onSelect()))}
                    >
                        {action.icon && <span className={commandItemIconClass}>{action.icon}</span>}
                        <span>{action.label}</span>
                    </CommandItem>
                ))}
            </CommandGroup>

            {renderActionGroup("danger", "Danger Zone")}
        </>
    );
};
